/**
 * Retrieval primitives — pure. Chunk doctrine text, then rank chunks either by
 * keyword overlap (deterministic, works with AI off) or by cosine similarity
 * over embeddings (when AI is enabled). "No source ⇒ no answer": callers must
 * treat an empty/low-score result as "no applicable doctrine found".
 */

import type { DoctrineDocument } from './doctrine-docs.js';

export interface Chunk {
  docId: string;
  docTitle: string;
  /** Index of the chunk within its document. */
  index: number;
  text: string;
  /** Embedding vector (set during ingestion when AI is enabled). */
  vector?: number[];
}

export interface Citation {
  docId: string;
  docTitle: string;
  snippet: string;
  score: number;
}

const STOP = new Set([
  'the', 'a', 'an', 'and', 'or', 'of', 'to', 'in', 'for', 'on', 'is', 'are', 'be',
  'with', 'as', 'at', 'by', 'this', 'that', 'it', 'from', 'should', 'may', 'must',
  'what', 'does', 'do', 'how', 'when', 'where', 'which',
]);

export function tokenize(text: string): string[] {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, ' ')
    .split(/\s+/)
    .filter((t) => t.length > 1 && !STOP.has(t));
}

/** Split text into chunks of roughly `maxChars`, breaking on paragraph/sentence. */
export function chunkText(text: string, maxChars = 400): string[] {
  const paras = text
    .split(/\n{2,}/)
    .map((p) => p.replace(/\s+/g, ' ').trim())
    .filter(Boolean);
  const chunks: string[] = [];
  for (const para of paras) {
    if (para.length <= maxChars) {
      chunks.push(para);
      continue;
    }
    // Break long paragraphs on sentence boundaries.
    let current = '';
    for (const sentence of para.split(/(?<=[.!?])\s+/)) {
      if ((current + ' ' + sentence).trim().length > maxChars && current) {
        chunks.push(current.trim());
        current = sentence;
      } else {
        current = `${current} ${sentence}`.trim();
      }
    }
    if (current) chunks.push(current.trim());
  }
  return chunks;
}

/** Build chunks for a set of (already applicability-filtered) documents. */
export function buildChunks(docs: readonly DoctrineDocument[], maxChars = 400): Chunk[] {
  const out: Chunk[] = [];
  for (const doc of docs) {
    chunkText(doc.body, maxChars).forEach((text, index) => {
      out.push({ docId: doc.id, docTitle: doc.title, index, text });
    });
  }
  return out;
}

/** Deterministic keyword overlap score in [0,1]. */
export function keywordScore(query: string, text: string): number {
  const q = tokenize(query);
  if (q.length === 0) return 0;
  const t = new Set(tokenize(text));
  let hits = 0;
  for (const term of new Set(q)) if (t.has(term)) hits += 1;
  return hits / new Set(q).size;
}

export function cosineSimilarity(a: number[], b: number[]): number {
  if (a.length !== b.length || a.length === 0) return 0;
  let dot = 0;
  let na = 0;
  let nb = 0;
  for (let i = 0; i < a.length; i++) {
    dot += a[i]! * b[i]!;
    na += a[i]! * a[i]!;
    nb += b[i]! * b[i]!;
  }
  if (na === 0 || nb === 0) return 0;
  return dot / (Math.sqrt(na) * Math.sqrt(nb));
}

export interface RetrieveOptions {
  k?: number;
  /** Minimum score to be considered a source (drives no-source⇒no-answer). */
  minScore?: number;
  /** If provided (and chunks carry vectors), rank by cosine instead of keywords. */
  queryVector?: number[];
}

/** Rank chunks for a query and return citations (highest score first). */
export function retrieve(query: string, chunks: readonly Chunk[], opts: RetrieveOptions = {}): Citation[] {
  const k = opts.k ?? 3;
  const minScore = opts.minScore ?? 0.01;
  const useVectors = opts.queryVector != null && chunks.every((c) => c.vector != null);

  const scored = chunks.map((c) => ({
    c,
    score: useVectors ? cosineSimilarity(opts.queryVector!, c.vector!) : keywordScore(query, c.text),
  }));

  return scored
    .filter((s) => s.score >= minScore)
    .sort((a, b) => b.score - a.score)
    .slice(0, k)
    .map(({ c, score }) => ({
      docId: c.docId,
      docTitle: c.docTitle,
      snippet: c.text,
      score: Math.round(score * 1000) / 1000,
    }));
}
